// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
    Fish,
    Printer,
    CheckCircle2,
    Minus,
    Plus,
    ExternalLink,
    ChevronDown,
    ChevronUp,
    Search,
    Receipt,
} from "lucide-react"
import Spinner from "react-bootstrap/Spinner"
import Button from "../common/Button.jsx"
import Toast from "../common/Toast.jsx"
import V2PageContainer from "../common/V2PageContainer.jsx"
import { BoxLabels, BoxLabelsProgressModal } from "../box-labels/index.js"
import useNotImplementedToast from "../hooks/useNotImplementedToast.js"
import {
    readPrintProcessingReceiptPref,
    writePrintProcessingReceiptPref,
} from "./fishRoomPrefs.js"
import {
    openProcessingReceiptPdf,
    openProcessingReceiptsPdf,
} from "./buildProcessingReceiptPdf.js"
import { ContactService } from "../services/contactService.js"
import { SpeciesService } from "../services/speciesService.js"
import { printFishTagLabel, printFishTagLabels } from "./printFishTagLabel.js"
import "./fish-room.css"

const ACCENT = "#0f6170"
const CURRENT_YEAR = new Date().getFullYear()
const RECENT_TRIPS_FIRST_ROW_COUNT = 6
const ORDERS_PER_PAGE = 100

function normalizeTripCode(raw) {
    return String(raw ?? "").trim().toUpperCase()
}

function compareTripCodesByLatestOrder(a, b) {
    const at = new Date(a.latest_order_at ?? 0).getTime()
    const bt = new Date(b.latest_order_at ?? 0).getTime()
    return bt - at
}

/** Most recent trip code for a year (by latest_order_at), or null if none. */
function mostRecentTripCodeForYear(tripCodes, year) {
    const match = tripCodes
        .filter((t) => t.year === year && normalizeTripCode(t.code).length > 0)
        .sort(compareTripCodesByLatestOrder)[0]
    return match ? normalizeTripCode(match.code) : null
}

function tripCodeExistsForYear(tripCodes, year, rawCode) {
    const code = normalizeTripCode(rawCode)
    if (!code || !Number.isFinite(year)) return false
    return tripCodes.some(
        (t) => t.year === year && normalizeTripCode(t.code) === code
    )
}

function recentTripCodesListForYear(tripCodes, year) {
    return tripCodes
        .filter((t) => t.year === year)
        .sort(compareTripCodesByLatestOrder)
        .map((t) => normalizeTripCode(t.code))
}

/** True when the trip's quick-pick button lives in the collapsed "more" row. */
function shouldExpandRecentTripsForCode(codesForYear, code) {
    if (!code) return false
    const index = codesForYear.indexOf(normalizeTripCode(code))
    return index >= RECENT_TRIPS_FIRST_ROW_COUNT
}

function fishTagSortKey(fishTag) {
    const parts = String(fishTag ?? "")
        .split(/[,/]+/)
        .map((s) => s.trim())
        .filter(Boolean)
    const nums = parts
        .map((p) => Number.parseInt(p.replace(/\D/g, ""), 10))
        .filter((n) => Number.isFinite(n))
    if (nums.length === 0) return Number.MAX_SAFE_INTEGER
    return Math.min(...nums)
}

function compareFishTagsForSort(a, b) {
    const ka = fishTagSortKey(a.fish_tag)
    const kb = fishTagSortKey(b.fish_tag)
    if (ka !== kb) return ka - kb
    return String(a.fish_tag ?? "").localeCompare(String(b.fish_tag ?? ""), undefined, {
        numeric: true,
    })
}

function isFishRoomOrderCompleted(order) {
    return Boolean(order?.box_count_at)
}

function formatLodgeShortCode(order, app, helpers) {
    const location = helpers?.getCurrentLocation?.(order, app?.locations ?? [])
    return location?.weigh_in_code ?? location?.short_code ?? "—"
}

function formatCustomerName(order) {
    return ContactService.getPrimaryName(order).lastCommaFirst()
}

function formatSpecies(order, app, { short = false } = {}) {
    const rows = (order?.details ?? []).map((detail) => {
        const species = (app?.species ?? []).find((x) => x.id === detail.species_id)
        if (species) {
            return short ? SpeciesService.shortFriendlyName(species) : species.name
        }
        return detail.other_species_name ?? ""
    }).filter(Boolean)
    return rows.length > 0 ? rows.join(", ") : "—"
}

async function fetchAllOrdersForTrip(api, tripCode, year) {
    const filters = [`{year = ${year}}`, `{trip_code = ${tripCode}}`]
    let page = 1
    const all = []

    while (true) {
        const body = await api.getOrders(
            page,
            ORDERS_PER_PAGE,
            "fish_tag",
            "asc",
            "",
            filters,
            { include: "details,options" }
        )
        const batch = body?.data ?? []
        all.push(...batch)
        const total = body?.total ?? batch.length
        if (all.length >= total || batch.length < ORDERS_PER_PAGE) {
            break
        }
        page += 1
    }

    return all
}

export default function FishRoomPage({ api, app, helpers, onEditOrder }) {
    const { toastProps, showNotImplementedToast } = useNotImplementedToast()
    const [tripCodes, setTripCodes] = useState([])
    const [isLoadingTripCodes, setIsLoadingTripCodes] = useState(true)
    const [tripCodesError, setTripCodesError] = useState(null)

    const [tripInput, setTripInput] = useState("")
    const [filterYear, setFilterYear] = useState(CURRENT_YEAR)
    /** Set when a recent trip is picked or Load orders is submitted — drives auto fetch. */
    const [selectedTripCode, setSelectedTripCode] = useState(null)
    const [activeTripCode, setActiveTripCode] = useState(null)
    const [activeYear, setActiveYear] = useState(null)
    const [tripOrders, setTripOrders] = useState([])
    const [isLoadingOrders, setIsLoadingOrders] = useState(false)
    const [selectedOrder, setSelectedOrder] = useState(null)
    const [totalBoxes, setTotalBoxes] = useState(1)
    const [copies, setCopies] = useState(1)
    const [printProcessingReceipt, setPrintProcessingReceipt] = useState(
        readPrintProcessingReceiptPref
    )
    const [isSavingOrderBoxes, setIsSavingOrderBoxes] = useState(false)
    const [labelsToPrint, setLabelsToPrint] = useState([])
    const [labelsIncludeReceipt, setLabelsIncludeReceipt] = useState(false)
    const [renderBoxLabelsPdf, setRenderBoxLabelsPdf] = useState(false)
    const [showBoxLabelsModal, setShowBoxLabelsModal] = useState(false)
    const [boxLabelProgress, setBoxLabelProgress] = useState(0)
    const cancelBoxLabelsRef = useRef(false)
    const prevFilterYearRef = useRef(null)
    const [recentTripsExpanded, setRecentTripsExpanded] = useState(false)
    const [pendingTab, setPendingTab] = useState("stJeans")
    const [selectedOrderIds, setSelectedOrderIds] = useState(() => new Set())
    const [bulkActionsOpen, setBulkActionsOpen] = useState(false)
    const [isBulkPrinting, setIsBulkPrinting] = useState(false)
    const [bulkPrintError, setBulkPrintError] = useState(null)
    const bulkActionsRef = useRef(null)
    const [takeHomeCustomers, setTakeHomeCustomers] = useState([])
    const [isLoadingCustomers, setIsLoadingCustomers] = useState(false)
    const [takeHomeError, setTakeHomeError] = useState(null)
    const [fishTagPrintingId, setFishTagPrintingId] = useState(null)
    const [selectedTakeHomeIds, setSelectedTakeHomeIds] = useState(() => new Set())
    const [isTakeHomeBulkPrinting, setIsTakeHomeBulkPrinting] = useState(false)
    const [takeHomeBulkPrintError, setTakeHomeBulkPrintError] = useState(null)

    useEffect(() => {
        let cancelled = false

        async function loadTripCodes() {
            setIsLoadingTripCodes(true)
            setTripCodesError(null)
            try {
                const body = await api.getTripCodes()
                if (cancelled) return
                const list = body?.data ?? []
                setTripCodes(list)
                const years = [...new Set(list.map((t) => t.year).filter(Number.isFinite))].sort(
                    (a, b) => b - a
                )
                const defaultYear = years.includes(CURRENT_YEAR)
                    ? CURRENT_YEAR
                    : years[0] ?? CURRENT_YEAR
                setFilterYear(defaultYear)

                const defaultTripCode = mostRecentTripCodeForYear(list, defaultYear)
                if (defaultTripCode) {
                    setSelectedTripCode(defaultTripCode)
                    setTripInput(defaultTripCode)
                }
            } catch (e) {
                if (!cancelled) {
                    setTripCodesError(e?.message ?? "Failed to load trip codes")
                    setTripCodes([])
                }
            } finally {
                if (!cancelled) {
                    setIsLoadingTripCodes(false)
                }
            }
        }

        loadTripCodes()
        return () => {
            cancelled = true
        }
    }, [api])

    useEffect(() => {
        if (isLoadingTripCodes || !Number.isFinite(filterYear)) {
            return
        }

        const prevYear = prevFilterYearRef.current
        prevFilterYearRef.current = filterYear
        if (prevYear === null || prevYear === filterYear) {
            return
        }

        const currentCode = selectedTripCode ? normalizeTripCode(selectedTripCode) : null
        const nextCode =
            currentCode && tripCodeExistsForYear(tripCodes, filterYear, currentCode)
                ? currentCode
                : mostRecentTripCodeForYear(tripCodes, filterYear)

        const codesForYear = recentTripCodesListForYear(tripCodes, filterYear)
        setRecentTripsExpanded(shouldExpandRecentTripsForCode(codesForYear, nextCode))

        if (nextCode) {
            setSelectedTripCode(nextCode)
            setTripInput(nextCode)
        } else {
            setSelectedTripCode(null)
            setTripInput("")
        }
    }, [filterYear, tripCodes, selectedTripCode, isLoadingTripCodes])

    const yearOptions = useMemo(() => {
        const years = [...new Set(tripCodes.map((t) => t.year).filter(Number.isFinite))]
        return years.sort((a, b) => b - a)
    }, [tripCodes])

    const recentTripCodesForYear = useMemo(
        () => recentTripCodesListForYear(tripCodes, filterYear),
        [tripCodes, filterYear]
    )

    const recentTripsFirstRow = useMemo(
        () => recentTripCodesForYear.slice(0, RECENT_TRIPS_FIRST_ROW_COUNT),
        [recentTripCodesForYear]
    )

    const recentTripsMoreRow = useMemo(
        () => recentTripCodesForYear.slice(RECENT_TRIPS_FIRST_ROW_COUNT),
        [recentTripCodesForYear]
    )

    const hasMoreRecentTrips = recentTripsMoreRow.length > 0

    useEffect(() => {
        const code = selectedTripCode ? normalizeTripCode(selectedTripCode) : ""
        if (!code || !Number.isFinite(filterYear)) {
            setActiveTripCode(null)
            setActiveYear(null)
            setTripOrders([])
            setSelectedOrder(null)
            setTripInput("")
            return
        }

        let cancelled = false
        const year = filterYear

        setActiveTripCode(code)
        setActiveYear(year)
        setTripInput(code)
        setIsLoadingOrders(true)
        setIsLoadingCustomers(true)
        setTripOrders([])
        setSelectedOrder(null)
        setSelectedOrderIds(new Set())
        setBulkActionsOpen(false)
        setBulkPrintError(null)
        setTakeHomeCustomers([])
        setTakeHomeError(null)
        setSelectedTakeHomeIds(new Set())
        setTakeHomeBulkPrintError(null)

            // Orders and take-home customers load in parallel; each clears its own
            // loading flag independently so its tab can render as soon as it's ready.
            ; (async () => {
                try {
                    const orders = await fetchAllOrdersForTrip(api, code, year)
                    if (cancelled) return
                    setTripOrders(orders)
                } catch (e) {
                    if (cancelled) return
                    showNotImplementedToast(e?.message ?? "Failed to load orders for trip")
                    setTripOrders([])
                } finally {
                    if (!cancelled) {
                        setIsLoadingOrders(false)
                    }
                }
            })()

            ; (async () => {
                try {
                    const customers = await api.getCustomersByTripCode(code, year)
                    if (cancelled) return
                    setTakeHomeCustomers(customers ?? [])
                } catch (e) {
                    if (cancelled) return
                    setTakeHomeError(e?.message ?? "Failed to load take home customers")
                    setTakeHomeCustomers([])
                } finally {
                    if (!cancelled) {
                        setIsLoadingCustomers(false)
                    }
                }
            })()

        return () => {
            cancelled = true
        }
        // Only re-fetch when year or committed trip selection changes — not when callback identities change.
    }, [filterYear, selectedTripCode])

    useEffect(() => {
        if (!showBoxLabelsModal) {
            setRenderBoxLabelsPdf(false)
            setLabelsToPrint([])
            setBoxLabelProgress(0)
        }
    }, [showBoxLabelsModal])

    useEffect(() => {
        if (!showBoxLabelsModal) {
            return
        }
        if (boxLabelProgress !== 100) {
            return
        }
        const timeoutId = window.setTimeout(() => {
            setShowBoxLabelsModal(false)
        }, 500)
        return () => window.clearTimeout(timeoutId)
    }, [boxLabelProgress, showBoxLabelsModal])

    useEffect(() => {
        if (!bulkActionsOpen) return
        const onPointerDown = (event) => {
            if (bulkActionsRef.current?.contains(event.target)) return
            setBulkActionsOpen(false)
        }
        const onKeyDown = (event) => {
            if (event.key === "Escape") setBulkActionsOpen(false)
        }
        document.addEventListener("mousedown", onPointerDown)
        document.addEventListener("keydown", onKeyDown)
        return () => {
            document.removeEventListener("mousedown", onPointerDown)
            document.removeEventListener("keydown", onKeyDown)
        }
    }, [bulkActionsOpen])

    const showTripResults = activeTripCode != null && activeYear != null

    const stJeansOrders = useMemo(() => {
        if (!showTripResults || isLoadingOrders) return []
        return [...tripOrders].sort(compareFishTagsForSort)
    }, [showTripResults, isLoadingOrders, tripOrders])

    const printedCount = useMemo(
        () => stJeansOrders.filter((o) => isFishRoomOrderCompleted(o)).length,
        [stJeansOrders]
    )

    const selectedOrders = useMemo(
        () => stJeansOrders.filter((o) => selectedOrderIds.has(o.id)),
        [stJeansOrders, selectedOrderIds]
    )

    const takeHomeRows = useMemo(
        () => [...takeHomeCustomers].sort(compareFishTagsForSort),
        [takeHomeCustomers]
    )

    const openOrderModal = useCallback((order) => {
        setSelectedOrder(order)
        setTotalBoxes(
            order.total_boxes != null && Number.isFinite(order.total_boxes)
                ? order.total_boxes
                : 1
        )
        setCopies(1)
        setPrintProcessingReceipt(readPrintProcessingReceiptPref())
    }, [])

    const togglePrintProcessingReceipt = useCallback(() => {
        setPrintProcessingReceipt((current) => {
            const next = !current
            writePrintProcessingReceiptPref(next)
            return next
        })
    }, [])

    const closeModal = useCallback(() => {
        setSelectedOrder(null)
        setIsSavingOrderBoxes(false)
    }, [])

    const handleSaveOrderBoxes = useCallback(async () => {
        if (!selectedOrder || isSavingOrderBoxes) return

        setIsSavingOrderBoxes(true)
        try {
            const updatedOrder = await api.setOrderBoxes(selectedOrder.id, totalBoxes)

            setTripOrders((prev) =>
                prev.map((row) =>
                    row.id === selectedOrder.id ? updatedOrder ?? row : row
                )
            )
            const savedOrder = updatedOrder ?? selectedOrder
            setSelectedOrder(savedOrder)
            return savedOrder
        } catch (e) {
            showNotImplementedToast(e?.message ?? "Failed to save box count")
            return null
        } finally {
            setIsSavingOrderBoxes(false)
        }
    }, [
        api,
        selectedOrder,
        totalBoxes,
        isSavingOrderBoxes,
        showNotImplementedToast,
    ])

    const handleEditOrder = useCallback(() => {
        if (!selectedOrder) return
        if (onEditOrder) {
            onEditOrder(selectedOrder)
            return
        }
        showNotImplementedToast("Edit order is not implemented yet.")
    }, [selectedOrder, onEditOrder, showNotImplementedToast])

    const startBoxLabelsPrint = useCallback((orders, includeReceipt) => {
        setBoxLabelProgress(0)
        cancelBoxLabelsRef.current = false
        setLabelsIncludeReceipt(includeReceipt)
        setShowBoxLabelsModal(true)
        setLabelsToPrint(orders)
        window.setTimeout(() => setRenderBoxLabelsPdf(true), 200)
    }, [])

    const handlePrintBoxLabels = useCallback((order) => {
        startBoxLabelsPrint([order], printProcessingReceipt)
        closeModal()
    }, [startBoxLabelsPrint, printProcessingReceipt, closeModal])

    const handlePrintProcessingReceiptOnly = useCallback(async (order) => {
        const total = order?.total_boxes ?? totalBoxes
        await openProcessingReceiptPdf(order, total, app, helpers)
        closeModal()
    }, [app, helpers, totalBoxes, closeModal])

    const handleCancelBoxLabels = useCallback(() => {
        cancelBoxLabelsRef.current = true
        setRenderBoxLabelsPdf(false)
        setShowBoxLabelsModal(false)
    }, [])

    const toggleOrderSelected = useCallback((id) => {
        setSelectedOrderIds((prev) => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }, [])

    const toggleSelectAllOrders = useCallback(() => {
        setSelectedOrderIds((prev) => {
            const allSelected =
                stJeansOrders.length > 0 && stJeansOrders.every((o) => prev.has(o.id))
            if (allSelected) return new Set()
            return new Set(stJeansOrders.map((o) => o.id))
        })
    }, [stJeansOrders])

    /** Persist box counts for the selected orders (defaulting to 1) and return the saved rows. */
    const persistSelectedBoxCounts = useCallback(async (orders) => {
        const saved = []
        for (const order of orders) {
            const boxes =
                order.total_boxes != null && Number.isFinite(order.total_boxes)
                    ? order.total_boxes
                    : 1
            const updatedOrder = await api.setOrderBoxes(order.id, boxes)
            saved.push(updatedOrder ?? { ...order, total_boxes: boxes })
        }
        setTripOrders((prev) =>
            prev.map((row) => saved.find((s) => s.id === row.id) ?? row)
        )
        return saved
    }, [api])

    const handleBulkPrint = useCallback(async (mode) => {
        if (isBulkPrinting || selectedOrders.length === 0) return
        setBulkActionsOpen(false)
        setBulkPrintError(null)
        setIsBulkPrinting(true)
        try {
            if (mode === "receipts") {
                await openProcessingReceiptsPdf(selectedOrders, app, helpers)
            } else {
                const saved = await persistSelectedBoxCounts(selectedOrders)
                startBoxLabelsPrint(saved, mode === "both")
            }
        } catch (e) {
            setBulkPrintError(e?.message ?? "Bulk print failed.")
        } finally {
            setIsBulkPrinting(false)
        }
    }, [
        isBulkPrinting,
        selectedOrders,
        app,
        helpers,
        persistSelectedBoxCounts,
        startBoxLabelsPrint,
    ])

    const handlePrintFishTag = useCallback((customer) => {
        if (!customer) return
        setFishTagPrintingId(customer.id ?? customer.fish_tag ?? null)
        try {
            printFishTagLabel(customer)
        } finally {
            // The browser print dialog is modal/synchronous; clear the
            // per-row spinner shortly after it is dismissed.
            window.setTimeout(() => setFishTagPrintingId(null), 500)
        }
    }, [])

    const selectedTakeHomeCustomers = useMemo(
        () =>
            takeHomeRows.filter((c) =>
                selectedTakeHomeIds.has(c.id ?? c.fish_tag)
            ),
        [takeHomeRows, selectedTakeHomeIds]
    )

    const toggleTakeHomeSelected = useCallback((id) => {
        setSelectedTakeHomeIds((prev) => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }, [])

    const toggleSelectAllTakeHome = useCallback(() => {
        setSelectedTakeHomeIds((prev) => {
            const ids = takeHomeRows.map((c) => c.id ?? c.fish_tag)
            const allSelected = ids.length > 0 && ids.every((id) => prev.has(id))
            if (allSelected) return new Set()
            return new Set(ids)
        })
    }, [takeHomeRows])

    const handleTakeHomeBulkPrint = useCallback(() => {
        if (isTakeHomeBulkPrinting || selectedTakeHomeCustomers.length === 0) return
        setTakeHomeBulkPrintError(null)
        setIsTakeHomeBulkPrinting(true)
        try {
            printFishTagLabels(selectedTakeHomeCustomers)
        } catch (error) {
            setTakeHomeBulkPrintError(
                error instanceof Error ? error.message : "Bulk print failed."
            )
        } finally {
            // The native print dialog is modal/synchronous; reset shortly after.
            window.setTimeout(() => setIsTakeHomeBulkPrinting(false), 500)
        }
    }, [isTakeHomeBulkPrinting, selectedTakeHomeCustomers])

    function handleTripSubmit(event) {
        event.preventDefault()
        const code = normalizeTripCode(tripInput)
        if (!code) {
            setSelectedTripCode(null)
            setTripInput("")
            return
        }
        setSelectedTripCode(code)
    }

    function selectRecentTrip(code) {
        setTripInput(code)
        setSelectedTripCode(code)
    }

    function renderRecentTripButton(code) {
        const isActive = activeTripCode === code && activeYear === filterYear
        return (
            <button
                key={code}
                type="button"
                className={
                    "btn lfr-quick-trip " +
                    (isActive ? "btn-primary text-white" : "btn-outline-secondary")
                }
                style={
                    isActive
                        ? { backgroundColor: ACCENT, borderColor: ACCENT }
                        : undefined
                }
                onClick={() => selectRecentTrip(code)}
            >
                {code}
            </button>
        )
    }

    const totalBoxesHint =
        totalBoxes === 1
            ? "Prints one label: Box 1 of 1."
            : `Prints ${totalBoxes} labels: Box 1 of ${totalBoxes} through Box ${totalBoxes} of ${totalBoxes}.`

    const noResults = showTripResults && !isLoadingOrders && tripOrders.length === 0

    if (isLoadingTripCodes) {
        return (
            <V2PageContainer>
                <Toast {...toastProps} />
                <div className="d-flex align-items-center gap-2 mb-4">
                    <Fish size={28} className="text-secondary d-none d-sm-block" />
                    <h1 className="fs-2 fw-bold text-dark mb-0">Lodge fish room</h1>
                </div>
                <div className="d-flex justify-content-center align-items-center py-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading trip codes…</span>
                    </Spinner>
                </div>
            </V2PageContainer>
        )
    }

    return (
        <V2PageContainer>
            <BoxLabels
                orders={labelsToPrint}
                locations={app?.locations ?? []}
                helpers={helpers}
                openPdf={renderBoxLabelsPdf}
                setOpenPdf={setRenderBoxLabelsPdf}
                progress={boxLabelProgress}
                setProgress={setBoxLabelProgress}
                cancelRef={cancelBoxLabelsRef}
                numCopies={copies}
                printProcessingReceipt={labelsIncludeReceipt}
                app={app}
            />
            <BoxLabelsProgressModal
                show={showBoxLabelsModal}
                progress={boxLabelProgress}
                onCancel={handleCancelBoxLabels}
            />
            <Toast {...toastProps} />

            <div className="d-flex align-items-center gap-2 mb-4">
                <Fish size={28} className="text-secondary d-none d-sm-block" />
                <h1 className="fs-2 fw-bold text-dark mb-0">Lodge fish room</h1>
            </div>

            {tripCodesError ? (
                <div className="alert alert-danger mb-4" role="alert">
                    {tripCodesError}
                </div>
            ) : null}

            <div className="bg-white rounded border p-3 p-md-4 mb-4">
                <form
                    onSubmit={handleTripSubmit}
                    className="d-flex flex-wrap align-items-end gap-3"
                >
                    <div className="lfr-field-year">
                        <label
                            htmlFor="lfr-year"
                            className="form-label small text-secondary mb-1"
                        >
                            Year
                        </label>
                        <select
                            id="lfr-year"
                            className="form-select"
                            value={filterYear}
                            onChange={(e) =>
                                setFilterYear(
                                    Number.parseInt(e.target.value, 10) || CURRENT_YEAR
                                )
                            }
                        >
                            {yearOptions.map((y) => (
                                <option key={y} value={y}>
                                    {y}
                                    {y === CURRENT_YEAR ? " (current)" : ""}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="lfr-field-trip">
                        <label
                            htmlFor="lfr-trip"
                            className="form-label small text-secondary mb-1"
                        >
                            Trip code
                        </label>
                        <input
                            id="lfr-trip"
                            type="text"
                            className="form-control font-monospace"
                            value={tripInput}
                            onChange={(e) => setTripInput(e.target.value)}
                            autoComplete="off"
                            spellCheck={false}
                        />
                    </div>
                    <Button type="submit" icon={<Search size={16} />}>
                        Load orders
                    </Button>
                </form>

                {recentTripCodesForYear.length > 0 ? (
                    <div className="mt-3 pt-3 border-top">
                        <p className="small text-secondary mb-2 mb-md-3">
                            Recent trips ({filterYear})
                        </p>
                        <div className="d-flex flex-wrap gap-2 gap-md-3 align-items-center">
                            {recentTripsFirstRow.map((code) => renderRecentTripButton(code))}
                            {hasMoreRecentTrips && !recentTripsExpanded && (
                                <button
                                    type="button"
                                    className="btn lfr-quick-trip btn-outline-secondary d-inline-flex align-items-center gap-2"
                                    onClick={() => setRecentTripsExpanded(true)}
                                    aria-expanded={false}
                                >
                                    Show more trips
                                    <ChevronDown size={22} aria-hidden />
                                </button>
                            )}
                        </div>
                        {hasMoreRecentTrips && recentTripsExpanded && (
                            <div className="d-flex flex-wrap gap-2 gap-md-3 align-items-center mt-2">
                                {recentTripsMoreRow.map((code) =>
                                    renderRecentTripButton(code)
                                )}
                                <button
                                    type="button"
                                    className="btn lfr-quick-trip btn-outline-secondary d-inline-flex align-items-center gap-2"
                                    onClick={() => setRecentTripsExpanded(false)}
                                    aria-expanded
                                >
                                    Show less
                                    <ChevronUp size={22} aria-hidden />
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <p className="small text-secondary mb-0 mt-3">
                        No recent trips for {filterYear}.
                    </p>
                )}

                <p className="small text-secondary mb-0 mt-3">
                    Trip codes repeat each season — choose a <strong>year</strong> and a{" "}
                    <strong>trip code</strong> (pick a recent trip to load automatically, or
                    type a code and use <strong>Load orders</strong>).
                </p>
            </div>

            {showTripResults && (
                <>
                    <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                        <span
                            className="badge rounded-pill"
                            style={{ backgroundColor: ACCENT, fontSize: "0.875rem" }}
                        >
                            {activeYear} · Trip {activeTripCode}
                        </span>
                        {!isLoadingOrders && !isLoadingCustomers ? (
                            <span className="small text-secondary">
                                {stJeansOrders.length} St Jean&apos;s · {printedCount} printed ·{" "}
                                {takeHomeRows.length} take home
                            </span>
                        ) : null}
                    </div>

                    <div className="bg-white rounded border p-3 p-md-4 mb-4">
                        <h2 className="fs-6 fw-semibold mb-3">
                            Orders | Trip {activeTripCode}
                        </h2>

                        <ul className="nav nav-tabs lfr-pending-tabs mb-3" role="tablist">
                            <li className="nav-item" role="presentation">
                                <button
                                    type="button"
                                    className={
                                        "nav-link" +
                                        (pendingTab === "stJeans" ? " active" : "")
                                    }
                                    onClick={() => setPendingTab("stJeans")}
                                    role="tab"
                                    aria-selected={pendingTab === "stJeans"}
                                >
                                    St Jean&apos;s Orders
                                    <span className="badge text-bg-secondary ms-2">
                                        {isLoadingOrders ? (
                                            <Spinner
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden
                                                style={{
                                                    width: "0.7rem",
                                                    height: "0.7rem",
                                                    borderWidth: "0.12em",
                                                }}
                                            />
                                        ) : (
                                            stJeansOrders.length
                                        )}
                                    </span>
                                </button>
                            </li>
                            <li className="nav-item" role="presentation">
                                <button
                                    type="button"
                                    className={
                                        "nav-link" +
                                        (pendingTab === "takeHome" ? " active" : "")
                                    }
                                    onClick={() => setPendingTab("takeHome")}
                                    role="tab"
                                    aria-selected={pendingTab === "takeHome"}
                                >
                                    Take Home
                                    {isLoadingCustomers ? (
                                        <span className="badge text-bg-secondary ms-2">
                                            <Spinner
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden
                                                style={{
                                                    width: "0.7rem",
                                                    height: "0.7rem",
                                                    borderWidth: "0.12em",
                                                }}
                                            />
                                        </span>
                                    ) : (
                                        <span className="badge text-bg-secondary ms-2">
                                            {takeHomeRows.length}
                                        </span>
                                    )}
                                </button>
                            </li>
                        </ul>

                        {pendingTab === "stJeans" ? (
                            isLoadingOrders ? (
                                <div className="d-flex justify-content-center align-items-center py-5">
                                    <Spinner animation="border" role="status">
                                        <span className="visually-hidden">
                                            Loading orders…
                                        </span>
                                    </Spinner>
                                </div>
                            ) : noResults ? (
                                <p className="small text-secondary mb-0">
                                    No orders found for trip{" "}
                                    <strong>{activeTripCode}</strong> in{" "}
                                    <strong>{activeYear}</strong>.
                                </p>
                            ) : (
                                <>
                                    <div className="d-flex align-items-center justify-content-between gap-3 mb-3 flex-wrap">
                                        <p className="small text-secondary mb-0">
                                            Select orders for bulk print, or click a row
                                            to open one order.
                                        </p>
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                            <span className="small text-secondary">
                                                {selectedOrderIds.size} selected
                                            </span>
                                            <div
                                                className="dropdown lfr-bulk-actions"
                                                ref={bulkActionsRef}
                                            >
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                                                    disabled={
                                                        selectedOrderIds.size === 0 ||
                                                        isBulkPrinting
                                                    }
                                                    aria-expanded={bulkActionsOpen}
                                                    onClick={() =>
                                                        setBulkActionsOpen((open) => !open)
                                                    }
                                                >
                                                    {isBulkPrinting ? (
                                                        <Spinner
                                                            animation="border"
                                                            size="sm"
                                                            role="status"
                                                            aria-hidden
                                                        />
                                                    ) : null}
                                                    Actions
                                                    <ChevronDown size={16} aria-hidden />
                                                </button>
                                                {bulkActionsOpen && (
                                                    <ul className="dropdown-menu dropdown-menu-end show">
                                                        <li>
                                                            <button
                                                                type="button"
                                                                className="dropdown-item"
                                                                onClick={() =>
                                                                    void handleBulkPrint(
                                                                        "labels"
                                                                    )
                                                                }
                                                            >
                                                                <Printer
                                                                    size={16}
                                                                    aria-hidden
                                                                />
                                                                Print box labels
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button
                                                                type="button"
                                                                className="dropdown-item"
                                                                onClick={() =>
                                                                    void handleBulkPrint(
                                                                        "receipts"
                                                                    )
                                                                }
                                                            >
                                                                <Receipt
                                                                    size={16}
                                                                    aria-hidden
                                                                />
                                                                Print processing receipts
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button
                                                                type="button"
                                                                className="dropdown-item"
                                                                onClick={() =>
                                                                    void handleBulkPrint(
                                                                        "both"
                                                                    )
                                                                }
                                                            >
                                                                <Printer
                                                                    size={16}
                                                                    aria-hidden
                                                                />
                                                                Print labels &amp; receipts
                                                            </button>
                                                        </li>
                                                    </ul>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {bulkPrintError ? (
                                        <div className="alert alert-danger py-2 small mb-3">
                                            {bulkPrintError}
                                        </div>
                                    ) : null}
                                    <FishRoomOrderTable
                                        rows={stJeansOrders}
                                        app={app}
                                        helpers={helpers}
                                        onSelect={openOrderModal}
                                        selectedIds={selectedOrderIds}
                                        onToggleSelect={toggleOrderSelected}
                                        onToggleSelectAll={toggleSelectAllOrders}
                                    />
                                </>
                            )
                        ) : isLoadingCustomers ? (
                            <div className="d-flex justify-content-center align-items-center py-5">
                                <Spinner animation="border" role="status">
                                    <span className="visually-hidden">
                                        Loading take home customers…
                                    </span>
                                </Spinner>
                            </div>
                        ) : takeHomeError ? (
                            <div className="alert alert-danger mb-0" role="alert">
                                {takeHomeError}
                            </div>
                        ) : takeHomeRows.length === 0 ? (
                            <p className="small text-secondary mb-0">
                                No take home customers found for trip{" "}
                                <strong>{activeTripCode}</strong>.
                            </p>
                        ) : (
                            <>
                                <div className="d-flex align-items-center justify-content-between gap-3 mb-3 flex-wrap">
                                    <p className="small text-secondary mb-0">
                                        Select guests for bulk print, or click{" "}
                                        <strong>Print</strong> on a row.
                                    </p>
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <span className="small text-secondary">
                                            {selectedTakeHomeIds.size} selected
                                        </span>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                                            disabled={
                                                selectedTakeHomeIds.size === 0 ||
                                                isTakeHomeBulkPrinting
                                            }
                                            onClick={handleTakeHomeBulkPrint}
                                        >
                                            {isTakeHomeBulkPrinting ? (
                                                <Spinner
                                                    animation="border"
                                                    size="sm"
                                                    role="status"
                                                    aria-hidden
                                                />
                                            ) : (
                                                <Printer size={16} aria-hidden />
                                            )}
                                            Print tags
                                        </button>
                                    </div>
                                </div>
                                {takeHomeBulkPrintError ? (
                                    <div className="alert alert-danger py-2 small mb-3">
                                        {takeHomeBulkPrintError}
                                    </div>
                                ) : null}
                                <FishRoomTakeHomeTable
                                    rows={takeHomeRows}
                                    accent={ACCENT}
                                    onPrint={handlePrintFishTag}
                                    printingId={fishTagPrintingId}
                                    selectedIds={selectedTakeHomeIds}
                                    onToggleSelect={toggleTakeHomeSelected}
                                    onToggleSelectAll={toggleSelectAllTakeHome}
                                />
                            </>
                        )}
                    </div>
                </>
            )}

            {selectedOrder && (
                <FishRoomOrderModal
                    order={selectedOrder}
                    app={app}
                    helpers={helpers}
                    totalBoxes={totalBoxes}
                    copies={copies}
                    totalBoxesHint={totalBoxesHint}
                    onTotalBoxesChange={setTotalBoxes}
                    onCopiesChange={setCopies}
                    onClose={closeModal}
                    onSave={handleSaveOrderBoxes}
                    isSaving={isSavingOrderBoxes}
                    onPrintLabels={handlePrintBoxLabels}
                    onPrintReceiptOnly={handlePrintProcessingReceiptOnly}
                    onEditOrder={handleEditOrder}
                    onNotImplemented={showNotImplementedToast}
                    accent={ACCENT}
                    editOrderImplemented={Boolean(onEditOrder)}
                    printProcessingReceipt={printProcessingReceipt}
                    onTogglePrintProcessingReceipt={togglePrintProcessingReceipt}
                />
            )}
        </V2PageContainer>
    )
}

function FishRoomOrderTable({
    rows,
    app,
    helpers,
    onSelect,
    selectedIds,
    onToggleSelect,
    onToggleSelectAll,
}) {
    const selectAllRef = useRef(null)
    const allSelected = rows.length > 0 && rows.every((row) => selectedIds.has(row.id))
    const someSelected = rows.some((row) => selectedIds.has(row.id))

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = someSelected && !allSelected
        }
    }, [someSelected, allSelected])

    if (rows.length === 0) return null

    return (
        <div className="table-responsive">
            <table className="table table-sm table-hover align-middle mb-0 lfr-orders-table lfr-orders-table--pending">
                <thead>
                    <tr>
                        <th className="lfr-row-select text-center">
                            <input
                                ref={selectAllRef}
                                type="checkbox"
                                className="form-check-input lfr-row-checkbox"
                                checked={allSelected}
                                onChange={onToggleSelectAll}
                                aria-label="Select all orders"
                            />
                        </th>
                        <th
                            className="text-uppercase text-secondary text-center"
                            style={{ fontSize: "0.6875rem" }}
                        >
                            Fish tag
                        </th>
                        <th
                            className="text-uppercase text-secondary"
                            style={{ fontSize: "0.6875rem" }}
                        >
                            Master #
                        </th>
                        <th
                            className="text-uppercase text-secondary"
                            style={{ fontSize: "0.6875rem" }}
                        >
                            Customer
                        </th>
                        <th
                            className="text-uppercase text-secondary"
                            style={{ fontSize: "0.6875rem" }}
                        >
                            Species
                        </th>
                        <th
                            className="text-uppercase text-secondary text-center"
                            style={{ fontSize: "0.6875rem" }}
                        >
                            Boxes
                        </th>
                        <th
                            className="text-uppercase text-secondary text-center"
                            style={{ fontSize: "0.6875rem" }}
                        >
                            Printed
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => {
                        const isSelected = selectedIds.has(row.id)
                        return (
                            <tr
                                key={row.id}
                                className={isSelected ? "table-active" : undefined}
                            >
                                <td
                                    className="lfr-row-select text-center"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <input
                                        type="checkbox"
                                        className="form-check-input lfr-row-checkbox"
                                        checked={isSelected}
                                        onChange={() => onToggleSelect(row.id)}
                                        aria-label={`Select order ${row.master ?? row.fish_tag ?? ""}`}
                                    />
                                </td>
                                <td
                                    className="font-monospace text-center lfr-row-open"
                                    onClick={() => onSelect(row)}
                                >
                                    {row.fish_tag ?? ""}
                                </td>
                                <td
                                    className="font-monospace lfr-cell-data lfr-cell-master lfr-row-open"
                                    onClick={() => onSelect(row)}
                                >
                                    {row.master ?? "—"}
                                </td>
                                <td
                                    className="lfr-cell-data lfr-row-open"
                                    onClick={() => onSelect(row)}
                                >
                                    {formatCustomerName(row)}
                                </td>
                                <td
                                    className="lfr-cell-data lfr-row-open"
                                    style={{ maxWidth: 220 }}
                                    onClick={() => onSelect(row)}
                                >
                                    {formatSpecies(row, app, { short: true })}
                                </td>
                                <td
                                    className="font-monospace fw-semibold text-center col-boxes lfr-row-open"
                                    onClick={() => onSelect(row)}
                                >
                                    {row.total_boxes ?? "—"}
                                </td>
                                <td
                                    className="text-center col-printed lfr-row-open"
                                    onClick={() => onSelect(row)}
                                >
                                    {isFishRoomOrderCompleted(row) ? (
                                        <CheckCircle2
                                            size={18}
                                            className="text-success"
                                            aria-label="Labels printed"
                                        />
                                    ) : (
                                        <span className="text-secondary" aria-hidden>
                                            —
                                        </span>
                                    )}
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

function FishRoomTakeHomeTable({
    rows,
    accent,
    onPrint,
    printingId,
    selectedIds,
    onToggleSelect,
    onToggleSelectAll,
}) {
    const selectAllRef = useRef(null)
    const rowId = (customer) => customer.id ?? customer.fish_tag
    const allSelected =
        rows.length > 0 && rows.every((row) => selectedIds.has(rowId(row)))
    const someSelected = rows.some((row) => selectedIds.has(rowId(row)))

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = someSelected && !allSelected
        }
    }, [someSelected, allSelected])

    if (rows.length === 0) return null

    return (
        <div className="table-responsive">
            <table className="table table-sm table-hover align-middle mb-0 lfr-orders-table lfr-orders-table--pending">
                <thead>
                    <tr>
                        <th className="lfr-row-select text-center">
                            <input
                                ref={selectAllRef}
                                type="checkbox"
                                className="form-check-input lfr-row-checkbox"
                                checked={allSelected}
                                onChange={onToggleSelectAll}
                                aria-label="Select all take home guests"
                            />
                        </th>
                        <th
                            className="text-uppercase text-secondary text-center"
                            style={{ fontSize: "0.6875rem" }}
                        >
                            Fish tag
                        </th>
                        <th
                            className="text-uppercase text-secondary"
                            style={{ fontSize: "0.6875rem" }}
                        >
                            Customer
                        </th>
                        <th
                            className="text-uppercase text-secondary text-center"
                            style={{ fontSize: "0.6875rem" }}
                        >
                            St Jean&apos;s
                        </th>
                        <th
                            className="text-uppercase text-secondary text-center"
                            style={{ fontSize: "0.6875rem" }}
                        >
                            Print
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((customer) => {
                        const id = customer.id ?? customer.fish_tag
                        const isStJeans = (customer.total_orders ?? 0) > 0
                        const isPrinting = printingId != null && printingId === id
                        const isSelected = selectedIds.has(id)
                        return (
                            <tr key={id} className={isSelected ? "table-active" : undefined}>
                                <td
                                    className="lfr-row-select text-center"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <input
                                        type="checkbox"
                                        className="form-check-input lfr-row-checkbox"
                                        checked={isSelected}
                                        onChange={() => onToggleSelect(id)}
                                        aria-label={`Select take home guest ${customer.fish_tag ?? ""
                                            }`}
                                    />
                                </td>
                                <td className="font-monospace text-center">
                                    {customer.fish_tag ?? ""}
                                </td>
                                <td className="lfr-cell-data">
                                    {formatCustomerName(customer)}
                                </td>
                                <td className="text-center col-take-home-type">
                                    {isStJeans ? (
                                        <span
                                            className="badge text-white"
                                            style={{ backgroundColor: accent }}
                                        >
                                            St Jean&apos;s
                                        </span>
                                    ) : (
                                        <span className="badge text-bg-light border text-secondary">
                                            Take home only
                                        </span>
                                    )}
                                </td>
                                <td className="text-center">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center justify-content-center gap-2"
                                        disabled={isPrinting}
                                        onClick={() => onPrint(customer)}
                                    >
                                        {isPrinting ? (
                                            <Spinner
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden
                                            />
                                        ) : (
                                            <Printer size={16} aria-hidden />
                                        )}
                                        Print
                                    </button>
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

function FishRoomTouchNumberStepper({
    id,
    label,
    value,
    onChange,
    min,
    max,
    hint,
    disabled = false,
}) {
    return (
        <div className="mb-4">
            <label className="form-label fw-semibold mb-2" htmlFor={id}>
                {label}
            </label>
            <div className="d-flex align-items-stretch lfr-touch-stepper">
                <button
                    type="button"
                    className="btn btn-outline-secondary lfr-step-btn flex-grow-1"
                    onClick={() => onChange(Math.max(min, value - 1))}
                    disabled={disabled || value <= min}
                    aria-label={`Decrease ${label}`}
                >
                    <Minus size={32} strokeWidth={2.5} aria-hidden />
                </button>
                <div
                    id={id}
                    className="lfr-step-value font-monospace flex-shrink-0"
                    aria-live="polite"
                    aria-atomic="true"
                >
                    {value}
                </div>
                <button
                    type="button"
                    className="btn btn-outline-secondary lfr-step-btn flex-grow-1"
                    onClick={() => onChange(Math.min(max, value + 1))}
                    disabled={disabled || value >= max}
                    aria-label={`Increase ${label}`}
                >
                    <Plus size={32} strokeWidth={2.5} aria-hidden />
                </button>
            </div>
            {hint ? <div className="form-text mt-2 mb-0">{hint}</div> : null}
        </div>
    )
}

function FishRoomOrderModal({
    order,
    app,
    helpers,
    totalBoxes,
    copies,
    totalBoxesHint,
    onTotalBoxesChange,
    onCopiesChange,
    onClose,
    onSave,
    isSaving = false,
    onPrintLabels,
    onPrintReceiptOnly,
    onEditOrder,
    onNotImplemented,
    accent,
    editOrderImplemented,
    printProcessingReceipt,
    onTogglePrintProcessingReceipt,
}) {
    const [isPrintingReceiptOnly, setIsPrintingReceiptOnly] = useState(false)
    const isBusy = isSaving || isPrintingReceiptOnly

    function mergeSavedOrder(saved) {
        return {
            ...order,
            ...saved,
            total_boxes: saved.total_boxes ?? totalBoxes,
            details: saved.details ?? order.details,
            options: saved.options ?? order.options,
        }
    }

    async function handleSaveAndPrint() {
        const saved = await onSave()
        if (!saved) return
        onPrintLabels(mergeSavedOrder(saved))
    }

    async function handleSaveAndPrintReceiptOnly() {
        if (isBusy) return
        const saved = await onSave()
        if (!saved) return
        setIsPrintingReceiptOnly(true)
        try {
            await onPrintReceiptOnly(mergeSavedOrder(saved))
        } catch (e) {
            onNotImplemented(e?.message ?? "Failed to print processing receipt")
        } finally {
            setIsPrintingReceiptOnly(false)
        }
    }
    return (
        <>
            <div
                className="modal-backdrop fade show"
                style={{ zIndex: 1090 }}
                onClick={onClose}
            />
            <div
                className="modal d-block"
                role="dialog"
                aria-modal="true"
                aria-labelledby="lfr-modal-title"
                style={{ zIndex: 1095 }}
                onClick={(e) => {
                    if (e.target === e.currentTarget) onClose()
                }}
            >
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content position-relative">
                        <div className="modal-header">
                            <h5 className="modal-title" id="lfr-modal-title">
                                {isFishRoomOrderCompleted(order)
                                    ? "Edit / Re-print"
                                    : "Box Labels"}{" "}
                                — {order.master ?? "—"}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                aria-label="Close"
                                onClick={onClose}
                            />
                        </div>
                        <div className="modal-body">
                            <div className="row g-3 mb-2">
                                <div className="col-md-7">
                                    <dl className="row small mb-0">
                                        <dt className="col-4 text-secondary">Trip</dt>
                                        <dd className="col-8 mb-1 font-monospace">
                                            {order.trip_code ?? ""}
                                        </dd>
                                        <dt className="col-4 text-secondary">Customer</dt>
                                        <dd className="col-8 mb-1">
                                            {formatCustomerName(order)}
                                        </dd>
                                        <dt className="col-4 text-secondary">Fish tag</dt>
                                        <dd className="col-8 mb-1">{order.fish_tag ?? ""}</dd>
                                        <dt className="col-4 text-secondary">Lodge</dt>
                                        <dd className="col-8 mb-1">
                                            {formatLodgeShortCode(order, app, helpers)}
                                        </dd>
                                        <dt className="col-4 text-secondary">Species</dt>
                                        <dd className="col-8 mb-1">
                                            {formatSpecies(order, app)}
                                        </dd>
                                        <dt className="col-4 text-secondary">Weight</dt>
                                        <dd className="col-8 mb-0">
                                            {order.total_weight_lb ?? "—"} lb
                                        </dd>
                                    </dl>
                                </div>
                                <div className="col-md-5 d-flex align-items-start justify-content-md-end">
                                    <div className="d-flex flex-column gap-2 w-100 w-md-auto">
                                        <button
                                            type="button"
                                            className={
                                                "btn btn-outline-secondary d-inline-flex align-items-center justify-content-center gap-2 w-100 lfr-edit-order-btn" +
                                                (editOrderImplemented
                                                    ? ""
                                                    : " v2-not-implemented-btn")
                                            }
                                            title={
                                                editOrderImplemented
                                                    ? undefined
                                                    : "Not implemented yet"
                                            }
                                            onClick={onEditOrder}
                                        >
                                            <ExternalLink size={20} aria-hidden />
                                            Edit order
                                        </button>
                                        <button
                                            type="button"
                                            id="lfr-print-processing-receipt"
                                            className={
                                                "btn d-inline-flex align-items-center justify-content-center gap-2 w-100 lfr-edit-order-btn " +
                                                (printProcessingReceipt
                                                    ? "lfr-receipt-toggle--on"
                                                    : "btn-outline-secondary")
                                            }
                                            style={
                                                printProcessingReceipt
                                                    ? {
                                                        backgroundColor: accent,
                                                        borderColor: accent,
                                                    }
                                                    : undefined
                                            }
                                            aria-pressed={printProcessingReceipt}
                                            disabled={isBusy}
                                            onClick={onTogglePrintProcessingReceipt}
                                        >
                                            <Receipt size={20} aria-hidden />
                                            Print Processing receipt
                                            {printProcessingReceipt ? (
                                                <CheckCircle2 size={20} aria-hidden />
                                            ) : null}
                                        </button>
                                        <button
                                            type="button"
                                            id="lfr-print-receipt-only"
                                            className="btn btn-outline-secondary d-inline-flex align-items-center justify-content-center gap-2 w-100 lfr-edit-order-btn"
                                            disabled={isBusy}
                                            onClick={() => void handleSaveAndPrintReceiptOnly()}
                                        >
                                            {isPrintingReceiptOnly ? (
                                                <Spinner
                                                    animation="border"
                                                    size="sm"
                                                    role="status"
                                                    aria-hidden
                                                />
                                            ) : (
                                                <Receipt size={20} aria-hidden />
                                            )}
                                            {isPrintingReceiptOnly
                                                ? "Preparing…"
                                                : "Print processing receipt only"}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <FishRoomTouchNumberStepper
                                id="lfr-total-boxes"
                                label="Total boxes"
                                value={totalBoxes}
                                onChange={onTotalBoxesChange}
                                min={1}
                                max={99}
                                hint={totalBoxesHint}
                            />

                            <FishRoomTouchNumberStepper
                                id="lfr-copies"
                                label="Copies of each box label"
                                value={copies}
                                onChange={onCopiesChange}
                                min={1}
                                max={20}
                                hint="Each box (e.g. 2 of 3) is printed this many times."
                            />
                        </div>
                        <div className="modal-footer lfr-modal-footer d-flex gap-3 pt-3 pb-3 px-3">
                            <button
                                type="button"
                                className="btn btn-outline-secondary lfr-modal-action flex-fill"
                                onClick={onClose}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary lfr-modal-action flex-fill d-inline-flex align-items-center justify-content-center gap-2"
                                style={{
                                    backgroundColor: accent,
                                    borderColor: accent,
                                }}
                                disabled={isBusy}
                                onClick={handleSaveAndPrint}
                            >
                                {isSaving ? (
                                    <Spinner
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden
                                    />
                                ) : (
                                    <Printer size={22} aria-hidden />
                                )}
                                {isSaving ? "Saving…" : "Save & Print"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
